import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { Brand, PersonStatus } from "@hubcapp/types";
import {
  INSTRUCTOR_ADI_NUMBER_LOCAL_STORAGE_KEY,
  IS_COGNITO_AUTH_ENABLED,
} from "@hubcapp/constants";
import authConfig, { BrowserStorage } from "../../auth.config";

const userPool = new CognitoUserPool(authConfig);

export const SMS_MFA_REQUIRED = "SMS_MFA";
export const LAMBDA_VALIDATION_EXCEPTION = "UserLambdaValidationException";
export const LIMIT_EXCEEDED_EXCEPTION = "LimitExceededException";

const noNumberRegex = /\D/g;

const getCurrentCognitoUserSession = (
  currentCognitoUser: CognitoUser,
): Promise<CognitoUserSession> =>
  new Promise((resolve, reject) => {
    currentCognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession) => {
        if (err) reject(err);
        resolve(session);
      },
    );
  });

export const getAuthToken = async () => {
  if (IS_COGNITO_AUTH_ENABLED) {
    const currentCognitoUser = userPool.getCurrentUser();
    if (currentCognitoUser) {
      return getCurrentCognitoUserSession(currentCognitoUser)
        .then(session => session.getAccessToken().getJwtToken())
        .catch(() => false);
    }

    return false;
  } else {
    //Format to ADI_NUMBER|ROLE eg. 700003006|INSTRUCTOR
    return `${window.localStorage.getItem(
      INSTRUCTOR_ADI_NUMBER_LOCAL_STORAGE_KEY,
    )}|INSTRUCTOR`;
  }
};

export const getUserId = () => userPool.getCurrentUser()?.getUsername() || "";

export const serializeBrands = (brands: Brand[]): string =>
  JSON.stringify(brands);

export const deserializeBrands = (serialized: string | null): Brand[] => {
  try {
    return JSON.parse(serialized || "");
  } catch (e) {
    return [Brand.Aads];
  }
};

export class Authentication {
  private user: CognitoUser;
  private authDetails: AuthenticationDetails;

  constructor(adiNumber: string) {
    this.user = new CognitoUser({
      Username: adiNumber,
      Pool: userPool,
      Storage: BrowserStorage,
    });

    this.authDetails = new AuthenticationDetails({
      Username: "",
      Password: "",
    });
  }

  signIn(
    adiNumber: string,
    password: string,
  ): Promise<CognitoUserSession | string> {
    this.authDetails = new AuthenticationDetails({
      Username: adiNumber,
      Password: password,
    });
    return new Promise((resolve, reject) => {
      this.user.authenticateUser(this.authDetails, {
        onSuccess: data => resolve(data),
        onFailure: err => reject(err),
        mfaRequired: data => resolve(data),
      });
    });
  }

  getAttributes(): Promise<{
    username: string;
    "custom:instructor_id": string;
    "custom:brand": string;
    given_name: string;
    family_name: string;
    "custom:status": PersonStatus;
  }> {
    return new Promise((resolve, reject) =>
      this.user.getUserAttributes((err, data) => {
        if (err) reject(data);

        const defaultAttributes = {
          username: this.user.getUsername(),
          "custom:instructor_id": "",
          "custom:brand": serializeBrands([Brand.Aads]),
          given_name: "",
          family_name: "",
          "custom:status": PersonStatus.Active,
        };

        const attributes =
          data?.reduce(
            (prev, current) => ({
              ...prev,
              [current.getName()]: current.getValue(),
            }),
            defaultAttributes,
          ) || defaultAttributes;

        resolve(attributes);
      }),
    );
  }

  isValidUserSession(): Promise<boolean> {
    return new Promise((resolve, reject) =>
      this.user.getSession((err: Error | null, session: CognitoUserSession) => {
        if (err) reject(err);
        resolve(session && session.isValid());
      }),
    );
  }

  resendMFACode() {
    return new Promise((resolve, reject) => {
      this.user.authenticateUser(this.authDetails, {
        onSuccess: data => resolve(data),
        onFailure: err => reject(err),
        mfaRequired: data => resolve(data),
      });
    });
  }

  confirmMFA(accessCode: string) {
    return new Promise((resolve, reject) => {
      this.user.sendMFACode(accessCode.replace(noNumberRegex, ""), {
        onSuccess: () => resolve(true),
        onFailure: err => reject(err),
      });
    });
  }

  sendMFAToResetPassword(mobileNumber: string) {
    return new Promise((resolve, reject) => {
      this.user.forgotPassword(
        {
          onSuccess: data => resolve(data),
          onFailure: err => {
            if (err.name === LAMBDA_VALIDATION_EXCEPTION) {
              return resolve(LAMBDA_VALIDATION_EXCEPTION);
            }
            if (err.name === LIMIT_EXCEEDED_EXCEPTION) {
              return resolve(LIMIT_EXCEEDED_EXCEPTION);
            }

            return reject(err);
          },
        },
        {
          phoneNumber: `+44${mobileNumber.substring(1)}`,
        },
      );
    });
  }

  resetPassword(accessCode: string, password: string) {
    return new Promise((resolve, reject) => {
      this.user.confirmPassword(
        accessCode.replace(noNumberRegex, ""), //Only numeric values accepted
        password,
        {
          onSuccess: () => resolve(true),
          onFailure: err => reject(err),
        },
      );
    });
  }

  signOut() {
    return new Promise(resolve => resolve(this.user.signOut()));
  }
}
